import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ShieldCheck, Lock, User, Eye, EyeOff, ArrowRight } from 'lucide-react'
import { useAuth } from '../auth'
import heroImg from '../assets/entradalogin.webp'

// Arcos (invisíveis) por onde viajam os "pacotes de dados" — origem no núcleo do globo
// (base central da imagem) subindo em leque. Só os pontos luminosos aparecem.
const ARCS = [
  'M240,530 Q120,380 130,180',
  'M240,530 Q200,340 205,120',
  'M240,530 Q300,340 290,150',
  'M240,530 Q380,380 372,225',
  'M240,530 Q150,420 120,300',
  'M240,530 Q340,430 360,330',
  'M240,530 Q250,380 250,210',
]
// partículas subindo (tráfego de dados) — concentradas no centro, sobre o globo
const SPARKS = Array.from({ length: 22 }, (_, i) => ({
  left: 26 + ((i * 29) % 50),
  delay: (i % 11) * 0.5,
  dur: 3.2 + (i % 5) * 0.7,
  size: 1.5 + (i % 3),
}))

export default function Login() {
  const { login } = useAuth()
  const nav = useNavigate()
  const [email, setEmail] = useState(() => localStorage.getItem('tc_remember') || '')
  const [senha, setSenha] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [lembrar, setLembrar] = useState(() => !!localStorage.getItem('tc_remember'))
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)

  async function submit(e) {
    e.preventDefault()
    setLoading(true)
    setErr('')
    try {
      await login(email.trim(), senha)
      if (lembrar) localStorage.setItem('tc_remember', email.trim())
      else localStorage.removeItem('tc_remember')
      nav('/dashboard')
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'CPF/e-mail ou senha incorretos')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex" style={{ background: '#f3f4f6' }}>
      <style>{`
        @keyframes tcRise { 0%{transform:translateY(0);opacity:0} 15%{opacity:.9} 85%{opacity:.6} 100%{transform:translateY(-240px);opacity:0} }
        @keyframes tcPulse { 0%,100%{transform:scale(1);opacity:.35} 50%{transform:scale(1.35);opacity:.6} }
        @keyframes tcSweep { 0%{transform:translateX(-60%) skewX(-12deg);opacity:0} 40%{opacity:.5} 100%{transform:translateX(160%) skewX(-12deg);opacity:0} }
        .tc-spark{position:absolute;bottom:14%;border-radius:9999px;background:#ffb347;box-shadow:0 0 8px 2px rgba(255,160,50,.7);animation:tcRise linear infinite}
      `}</style>

      {/* ESQUERDA — hero de marca (desktop). Imagem INTEIRA (contain, sem corte) + tráfego de dados.
          Fundo PRETO PURO p/ as bordas pretas da arte fundirem (sem emenda/barras visíveis). */}
      <div className="hidden lg:flex items-center justify-center relative overflow-hidden" style={{ width: '48%', background: '#000' }}>
        <img src={heroImg} alt="The Conect — tecnologia que impulsiona conexões" loading="eager" decoding="async"
          className="absolute inset-0 w-full h-full" style={{ objectFit: 'contain', objectPosition: 'center' }} />
        {/* camada de movimento (concentrada no centro/globo) */}
        <div className="absolute inset-0 pointer-events-none">
          {/* núcleo pulsante sobre o globo */}
          <div className="absolute rounded-full blur-3xl" style={{ left: '50%', top: '66%', width: 220, height: 220, transform: 'translate(-50%,-50%)', background: 'radial-gradient(circle, rgba(255,150,30,.45), transparent 70%)', animation: 'tcPulse 3.4s ease-in-out infinite' }} />
          {/* pacotes de dados subindo do globo (arcos leves) */}
          <svg viewBox="0 0 480 600" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full">
            <defs>
              <filter id="tcglow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="2.4" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {ARCS.map((p, i) => (
              <circle key={i} r={2.2} fill="#ffd27a" filter="url(#tcglow)" opacity="0">
                <animateMotion dur={`${3 + (i % 4) * 0.9}s`} begin={`${(i * 0.7).toFixed(1)}s`} repeatCount="indefinite" path={p} rotate="auto" />
                <animate attributeName="opacity" values="0;1;1;0" dur={`${3 + (i % 4) * 0.9}s`} begin={`${(i * 0.7).toFixed(1)}s`} repeatCount="indefinite" />
              </circle>
            ))}
          </svg>
          {/* partículas subindo */}
          {SPARKS.map((s, i) => (
            <span key={i} className="tc-spark" style={{ left: `${s.left}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s`, animationDuration: `${s.dur}s` }} />
          ))}
          {/* brilho passando (varredura) */}
          <div className="absolute inset-y-0 w-1/3" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,180,80,.08), transparent)', animation: 'tcSweep 7s ease-in-out infinite' }} />
        </div>
      </div>

      {/* DIREITA — formulário (card do mockup) */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* logo compacto no mobile (sem o hero) */}
          <div className="lg:hidden flex justify-center mb-6">
            <img src="/logothe.png" alt="The Conect" style={{ maxHeight: 54 }} />
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 p-8 sm:p-10" style={{ boxShadow: '0 30px 60px -20px rgba(20,20,30,0.18)' }}>
            <form onSubmit={submit}>
              <div className="flex flex-col items-center text-center mb-7">
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full mb-5 text-[11px] font-bold uppercase tracking-widest"
                  style={{ background: 'linear-gradient(135deg,#fff3d6,#ffe1c2)', color: '#b45309', border: '1px solid rgba(255,138,0,0.18)' }}>
                  <Lock size={12} /> Área Exclusiva
                </span>
                <h1 className="text-3xl font-heading font-extrabold text-gray-900" style={{ letterSpacing: '-0.02em' }}>Bem-vindo de volta</h1>
                <p className="text-sm text-gray-400 mt-1.5">Entre na sua conta para continuar</p>
              </div>

              {err && <div className="mb-5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}

              <label className="block text-sm font-semibold text-gray-800 mb-1.5">CPF ou E-mail</label>
              <div className="relative mb-4">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
                  placeholder="Digite seu CPF ou e-mail"
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 pl-11 pr-4 py-3.5 text-gray-800 outline-none focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 transition" />
              </div>

              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Senha</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showPass ? 'text' : 'password'} value={senha} onChange={(e) => setSenha(e.target.value)} required
                  placeholder="Digite sua senha"
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 pl-11 pr-11 py-3.5 text-gray-800 outline-none focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 transition" />
                <button type="button" onClick={() => setShowPass((v) => !v)} tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1" aria-label="Mostrar senha">
                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="mt-2 text-right">
                <Link to="/esqueci-senha" className="text-xs font-semibold hover:underline" style={{ color: '#ea6a0a' }}>Esqueci minha senha</Link>
              </div>

              <label className="flex items-center gap-2 mt-4 mb-5 cursor-pointer select-none">
                <input type="checkbox" checked={lembrar} onChange={(e) => setLembrar(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400 accent-orange-500" />
                <span className="text-sm text-gray-600">Lembrar meu acesso</span>
              </label>

              <button disabled={loading}
                className="w-full rounded-2xl text-white font-bold py-3.5 flex items-center justify-center gap-2 disabled:opacity-60 transition hover:brightness-105 active:scale-[0.99]"
                style={{ background: 'linear-gradient(90deg,#ffb020,#ff7a00)', boxShadow: '0 10px 24px -8px rgba(255,122,0,0.55)' }}>
                {loading ? 'Entrando...' : <>Entrar <ArrowRight size={18} /></>}
              </button>

              <div className="mt-7 pt-5 border-t border-gray-100 flex items-center justify-center gap-1.5 text-xs text-gray-400">
                <ShieldCheck size={13} className="text-gray-400" /> Ambiente seguro e criptografado
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
